/**
 * Closure Service
 *
 * Handles project closure operations including validation,
 * signature capture, and formal closure record creation.
 *
 * @module services/closureService
 */

import { supabase } from './supabase';
import { canCaptureSignatureType, signatureDenialMessage } from '../utils/constants';
import { getChecklists, createChecklist } from './checklistService';
import {
  CLOSURE_CHECKLIST_TYPE,
  CLOSURE_CHECKLIST_TITLE,
  CLOSURE_CHECKLIST_IS_REQUIRED,
  CLOSURE_CHECKLIST_ITEMS,
} from '../constants/closureChecklist';

/**
 * Validate project closure readiness
 * Calls the validate_project_closure RPC to check all prerequisites.
 *
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const validateClosure = async (projectId) => {
  try {
    const { data, error } = await supabase.rpc('validate_project_closure', {
      p_project_id: projectId,
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Validate closure error:', error);
    return { data: null, error };
  }
};

/**
 * Iniciar el cierre de un proyecto (equivalente web de `initiate_project_closure`).
 *
 * POR QUÉ EXISTE — paridad de carriles (CLAUDE.md, "Lane Parity"):
 * el carril MCP (`mcp-server/src/tools/closureTools.js`,
 * `initiate_project_closure`) valida Y SIEMBRA el checklist de cierre. El carril
 * web sólo validaba: `completeClosure` escribía `project_closures` y volteaba
 * `projects.status` sin tocar `project_checklists` jamás. El wizard mostraba una
 * lista inventada en `useState` que nunca se persistía.
 *
 * Medido en prod el 2026-07-30: `project_closures` = 0 filas. Nadie cerró nunca
 * un proyecto, así que el daño presente era cero — era una compuerta abierta
 * esperando el primer cierre real.
 *
 * CONTRATO DE ERRORES (esto es lo importante):
 * - Si la validación falla (RPC caída, RLS que rebota con 42501, red), esta
 *   función devuelve `{ data: null, error }`. **No inventa un veredicto.** El
 *   llamador tiene que BLOQUEAR el cierre. El wizard hardcodeaba
 *   `ready: true` + `completion_checklists_done: true` en sus dos ramas de
 *   fallback, o sea que un fallo de infraestructura habilitaba el cierre
 *   completo: sin firma del cliente, con tareas abiertas y con incidentes
 *   abiertos.
 * - Si la validación SÍ corrió pero el checklist no se pudo preparar, se
 *   devuelve el veredicto real más `checklistError`. Esa diferencia importa:
 *   ahí sí se sabe el estado del proyecto, así que un admin puede decidir
 *   informadamente (override). Con la validación caída no se sabe nada.
 *
 * @param {string} projectId - Project ID
 * @returns {Promise<{data: {validation, checklist, checklistCreated, checklistError}|null, error}>}
 */
export const initiateClosure = async (projectId) => {
  try {
    if (!projectId) throw new Error('Se requiere el proyecto');

    const { data: validation, error: rpcError } = await supabase.rpc(
      'validate_project_closure',
      { p_project_id: projectId }
    );

    if (rpcError) throw rpcError;
    if (!validation) {
      throw new Error('La verificacion de cierre no devolvio ningun resultado.');
    }

    let checklist = null;
    let checklistCreated = false;
    let checklistError = null;

    const { data: existing, error: listError } = await getChecklists(projectId, {
      checklistType: CLOSURE_CHECKLIST_TYPE,
    });

    if (listError) {
      checklistError = listError;
    } else if (existing && existing.length > 0) {
      checklist = existing[0];
    } else {
      const { data: created, error: createError } = await createChecklist({
        project_id: projectId,
        title: CLOSURE_CHECKLIST_TITLE,
        checklist_type: CLOSURE_CHECKLIST_TYPE,
        is_required: CLOSURE_CHECKLIST_IS_REQUIRED,
        items: CLOSURE_CHECKLIST_ITEMS,
      });

      if (createError) {
        checklistError = createError;
      } else if (!created) {
        checklistError = new Error('No se pudo preparar el checklist de cierre.');
      } else {
        checklist = created;
        checklistCreated = true;
      }
    }

    if (!checklist && !checklistError) {
      checklistError = new Error('No se pudo preparar el checklist de cierre.');
    }

    return {
      data: { validation, checklist, checklistCreated, checklistError },
      error: null,
    };
  } catch (error) {
    console.error('Initiate closure error:', error);
    return { data: null, error };
  }
};

/**
 * Save a client signature for a project
 *
 * DB table: project_signatures. Live columns are signature_data (TEXT,
 * stores the base64 PNG inline) and signed_by — not signature_image_url
 * / captured_by, which were never actually added to the live table
 * despite migration 027 intending them (see issue #305 and
 * supabase/migrations/20260705150000_fix_project_signatures_phantom_columns.sql).
 * signature_type must be one of the live CHECK constraint's values
 * (client_acceptance, project_closure, incident_acknowledgment,
 * invoice_receipt) — this flow is the client-facing signature captured
 * during project closure, so it uses 'project_closure'.
 *
 * @param {string} projectId - Project ID
 * @param {Object} signatureData - Signature data
 * @param {string} signatureData.signatureDataUrl - Base64 PNG of signature
 * @param {string} signatureData.signerName - Name of the signer
 * @param {string} signatureData.signerRole - Role of the signer
 * @param {string} signatureData.signerIdNumber - ID number (cedula)
 * @returns {Promise<{data, error}>}
 */
export const saveSignature = async (projectId, signatureData) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Compuerta de rol (#525). Este insert escribe `project_closure`
    // directamente, sin pasar por signatureService, así que necesita su propia
    // comprobación o el carril queda abierto. Cerrar es acto de la empresa
    // ("Cerrar proyecto: Líder ❌" en wiki/pages/role-ui-matrix.md); la RLS ya
    // lo rebota, esto explica por qué.
    const { data: callerProfile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();
    if (!canCaptureSignatureType(callerProfile?.role, 'project_closure')) {
      throw new Error(signatureDenialMessage('project_closure'));
    }

    // Insert signature record — signature_data stores the base64 PNG
    // inline, matching mcp-server/src/tools/signatureTools.js.
    const { data, error } = await supabase
      .from('project_signatures')
      .insert({
        project_id: projectId,
        signature_type: 'project_closure',
        signature_data: signatureData.signatureDataUrl,
        signer_name: signatureData.signerName,
        signer_role: signatureData.signerRole || null,
        signer_id_number: signatureData.signerIdNumber || null,
        signed_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Save signature error:', error);
    return { data: null, error };
  }
};

/**
 * Complete project closure
 * Creates the closure record and transitions the project to 'closed' status.
 *
 * @param {string} projectId - Project ID
 * @param {Object} closureData - Closure form data
 * @param {number} closureData.customerRating - 1-5 star rating
 * @param {string} closureData.customerFeedback - Client feedback text
 * @param {string} closureData.internalNotes - Admin-only notes
 * @param {string} closureData.completionSummary - Completion summary text
 * @param {string} closureData.clientSignatureId - UUID of the signature record
 * @param {number} closureData.quotedAmount - Quoted amount (snapshot)
 * @param {number} closureData.totalExpenses - Total expenses (snapshot)
 * @returns {Promise<{data, error}>}
 */
export const completeClosure = async (projectId, closureData) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const quotedAmount = closureData.quotedAmount || 0;
    const totalExpenses = closureData.totalExpenses || 0;
    const variance = quotedAmount - totalExpenses;
    const variancePercentage = quotedAmount > 0
      ? Math.round(((totalExpenses / quotedAmount) * 100) * 100) / 100
      : 0;

    // Insert closure record
    const { data: closure, error: closureError } = await supabase
      .from('project_closures')
      .insert({
        project_id: projectId,
        closed_by: user.id,
        quoted_amount: quotedAmount,
        total_expenses: totalExpenses,
        final_variance: variance,
        variance_percentage: variancePercentage,
        customer_rating: closureData.customerRating || null,
        customer_feedback: closureData.customerFeedback || null,
        internal_notes: closureData.internalNotes || null,
        completion_summary: closureData.completionSummary || null,
        client_signature_id: closureData.clientSignatureId || null,
      })
      .select()
      .single();

    if (closureError) throw closureError;

    // Update project status to closed
    const { error: projectError } = await supabase
      .from('projects')
      .update({
        status: 'closed',
        closed_at: new Date().toISOString(),
        closed_by: user.id,
      })
      .eq('id', projectId);

    if (projectError) throw projectError;

    return { data: closure, error: null };
  } catch (error) {
    console.error('Complete closure error:', error);
    return { data: null, error };
  }
};

/**
 * Get project closure record
 *
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getClosure = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('project_closures')
      .select(`
        *,
        closed_by_profile:profiles!closed_by(id, full_name),
        client_signature:project_signatures!client_signature_id(
          id, signer_name, signer_role, signer_id_number, signature_data, created_at
        )
      `)
      .eq('project_id', projectId)
      .maybeSingle();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get closure error:', error);
    return { data: null, error };
  }
};

const closureService = {
  validateClosure,
  initiateClosure,
  saveSignature,
  completeClosure,
  getClosure,
};

export default closureService;
